/**
 * Capabilities:
 * - CAP-MEMBER-ONBOARDING
 * - CAP-MEMBER-FAMILY-MANAGEMENT
 * - CAP-MEMBER-SELF-SERVICE
 * - CAP-VOUCHER-REDEMPTION
 * - CAP-PROMOTION-PARTICIPATION
 * - CAP-STAFF-MEMBER-ADMINISTRATION
 * - CAP-STAFF-VOUCHER-ADMINISTRATION
 * - CAP-STAFF-SCHEDULED-MESSAGING
 * - CAP-STAFF-AUDIT-REVIEW
 * - CAP-STAFF-METRICS
 * - CAP-STAFF-OPERATIONS-SUPPORT
 * - CAP-OUTLET-STOCK-CONTROL
 * - CAP-OUTLET-REDEMPTION-CODE
 *
 * Responsibilities:
 * - Provide the shared REST client used by the browser entry points.
 * - Attach Cognito ID tokens only for routes that require authenticated staff
 *   or protected member access.
 *
 * Context:
 * - project-intelligence/generated/api-routes.md
 * - project-intelligence/architecture/authentication-and-authorization.md
 * - project-intelligence/architecture/frontend-applications.md
 */

import { Auth } from 'aws-amplify';
import axios from 'axios';

let restEndpoint = null;

async function call(path, data, { needsAuth = false, method = 'GET', headers = {} } = {}) {
    /**
     * Shared browser-to-Chalice transport.
     *
     * The caller decides whether to attach Cognito auth so the same helper can
     * service public Telegram/member routes and protected staff routes without
     * duplicating endpoint selection or logging behavior.
     */
    if (!restEndpoint) {
        throw new Error('rest-endpoint-not-set')
    }
    const restEndpointRoot = restEndpoint.endsWith('/') ? restEndpoint.substr(0, restEndpoint.length - 1) : restEndpoint.rest;
    const url = `${restEndpointRoot}${path}`;
    const _headers = { ...headers };

    if (needsAuth) {
        const user = await Auth.currentAuthenticatedUser();
        const idToken = user.signInUserSession.idToken.jwtToken
        _headers.Authorization = idToken;    
    }

    return await axios({ method, url, headers: _headers, data })
        .then(r => {
            console.log('----\n', `${method} ${path}; Needs Auth: ${needsAuth}\n`, r.data)
            return r;
        }).catch(e => {
            console.error('----\n', `${method} ${path}; Needs Auth: ${needsAuth}\n`, e)
            throw e;
        });
}

async function postMultipartFormData(path, formData) {
    return await call(path, formData, { method: 'POST', headers: { 'Content-Type': 'multipart/form-data' } })
}

async function postMultipartFormDataWithAuth(path, formData) {
    return await call(path, formData, {
        needsAuth: true,
        method: 'POST',
        headers: { 'Content-Type': 'multipart/form-data' },
    })
}

async function callWithAuth(path, data, { method = 'GET' } = {}) {
    return await call(path, data, { needsAuth: true, method })
}

async function postWithAuth(path, data) {
    return await call(path, data, { needsAuth: true, method: 'POST' })
}

function setEndpoint(url) {
    /**
     * Frontend bootstrap contract: update the active API root when Amplify or
     * a custom domain changes the deployed REST endpoint.
     */
    if (restEndpoint !== url) {
        console.log(`Setting REST API Endpoint Url: ${url}`)
        restEndpoint = url;
    }
}

const exports = {
    setEndpoint,
    call,
    postMultipartFormData,
    postMultipartFormDataWithAuth,
    callWithAuth,
    postWithAuth,
};
Object.defineProperty(exports, 'endpoint', {
    get: () => restEndpoint,
})

export default exports;
